import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState, selectApplications } from '../store';
import { useTranslation } from '../i18n';
import { useSearchParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
} from 'recharts';
import { Rule, AuditLog } from '../types';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PortfolioHealth from './PortfolioHealth';
import TransactionReports from './TransactionReports';
import InactiveAccountAlerts from './InactiveAccountAlerts';
import AccountLinkingComponent from './AccountLinking';
import LoanHistory from './LoanHistory';
import RegulatoryReports from './RegulatoryReports';
import KYCReports from './KYCReports';
import AdvancedRiskDashboard from './AdvancedRiskDashboard';

// #region Helper Functions
const exportToXLS = (data: any[], fileName: string) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

const exportToPDF = (headers: string[][], body: any[][], fileName: string) => {
  const doc = new jsPDF();
  autoTable(doc, { head: headers, body });
  doc.save(`${fileName}.pdf`);
};
// #endregion

// #region Application Reports
const ApplicationReports = () => {
  const { t } = useTranslation();
  const applications = useSelector(selectApplications);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const filteredApplications = useMemo(() => {
    if (!Array.isArray(applications)) {
      return [];
    }
    
    return applications.filter((app) => {
      const statusMatch = statusFilter === 'all' || app.status === statusFilter;
      const appDate = new Date(app.submissionDate);
      const startMatch = !startDate || appDate >= startDate;
      const endMatch = !endDate || appDate <= endDate;
      return statusMatch && startMatch && endMatch;
    });
  }, [applications, statusFilter, startDate, endDate]);

  const handleDownloadXLS = () => {
    const data = filteredApplications.map(app => ({
      'ID': app.id,
      'Applicant Name': app.applicantName,
      'Customer Type': app.customerType,
      'Requested Amount': app.requestedAmount,
      'Submission Date': new Date(app.submissionDate).toLocaleDateString(),
      'Score': app.creditScore,
      'Status': app.status,
    }));
    exportToXLS(data, `applications-${statusFilter}`);
  };

  const handleDownloadPDF = () => {
    const head = [['ID', 'Applicant Name', 'Customer Type', 'Requested Amount', 'Submission Date', 'Score', 'Status']];
    const body = filteredApplications.map(app => [
      app.id,
      app.applicantName,
      app.customerType,
      app.requestedAmount,
      new Date(app.submissionDate).toLocaleDateString(),
      app.creditScore ?? 'N/A',
      app.status,
    ]);
    exportToPDF(head, body, `applications-${statusFilter}`);
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>{t('reports.downloadLoanApplications')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{t('reports.filterByStatus')}</InputLabel>
          <Select value={statusFilter} label={t('reports.filterByStatus')} onChange={(e) => setStatusFilter(e.target.value as any)}>
            <MenuItem value="all">{t('reports.all')}</MenuItem>
            <MenuItem value="pending">{t('reports.pending')}</MenuItem>
            <MenuItem value="approved">{t('reports.approved')}</MenuItem>
            <MenuItem value="rejected">{t('reports.rejected')}</MenuItem>
          </Select>
        </FormControl>
        <DatePicker label={t('common.startDate')} value={startDate} onChange={setStartDate} />
        <DatePicker label={t('common.endDate')} value={endDate} onChange={setEndDate} />
      </Box>
      <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleDownloadXLS} disabled={filteredApplications.length === 0}>
          {t('reports.downloadXLS')}
        </Button>
        <Button variant="contained" color="secondary" onClick={handleDownloadPDF} disabled={filteredApplications.length === 0}>
          {t('reports.downloadPDF')}
        </Button>
      </Box>
      <Typography variant="body1" sx={{ mt: 2 }}>
        Found {filteredApplications.length} application(s).
      </Typography>
    </Paper>
  );
};
// #endregion

// #region System Reports
const SystemReports = () => {
  const { t } = useTranslation();
  const auditLogs = useSelector((state: RootState) => state.auditLog.logs);
  const { rules, smeRules } = useSelector((state: RootState) => state.config);

  const handleDownloadAuditLog = (format: 'xls' | 'pdf') => {
    const data = auditLogs.map((log: AuditLog) => ({
      [t('reports.timestamp')]: new Date(log.timestamp).toLocaleString(),
      [t('reports.user')]: log.user,
      [t('reports.action')]: log.action,
      [t('reports.details')]: log.details ? JSON.stringify(log.details) : ''
    }));

    if (format === 'xls') {
      exportToXLS(data, 'audit-log');
    } else {
      const head = [[t('reports.timestamp'), t('reports.user'), t('reports.action'), t('reports.details')]];
      const body = data.map(d => [d.Timestamp, d.User, d.Action, d.Details]);
      exportToPDF(head, body, 'audit-log');
    }
  };

  const handleDownloadRules = (rulesToDownload: Rule[], type: 'individual' | 'sme') => {
    const data = rulesToDownload.map(rule => ({
      [t('reports.name')]: rule.name,
      [t('reports.description')]: rule.description,
      [t('reports.field')]: rule.field,
      [t('reports.operator')]: rule.operator,
      [t('reports.value')]: rule.value,
      [t('reports.score')]: rule.score,
    }));
    exportToXLS(data, `${type}-rules`);
  };

  return (
    <>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>{t('reports.downloadAuditLog')}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => handleDownloadAuditLog('xls')}>{t('reports.downloadXLS')}</Button>
          <Button variant="contained" color="secondary" onClick={() => handleDownloadAuditLog('pdf')}>{t('reports.downloadPDF')}</Button>
        </Box>
      </Paper>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>{t('reports.downloadRules')}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => handleDownloadRules(rules, 'individual')}>{t('rules.individualRules')} (XLS)</Button>
          <Button variant="outlined" onClick={() => handleDownloadRules(smeRules, 'sme')}>{t('rules.smeRules')} (XLS)</Button>
        </Box>
      </Paper>
    </>
  );
};
// #endregion

// #region Visualizations
const Visualizations = () => {
    const applications = useSelector(selectApplications);

    const statusData = useMemo(() => {
        const statuses = { approved: 0, pending: 0, rejected: 0, disbursed: 0 };
        applications.forEach(app => {
            if (app.status === 'approved' || app.status === 'disbursed') {
                statuses.approved++;
            } else if (app.status === 'pending') {
                statuses.pending++;
            } else if (app.status === 'rejected') {
                statuses.rejected++;
            }
        });
        return [
            { name: 'Approved/Disbursed', value: statuses.approved, color: '#4caf50' },
            { name: 'Pending', value: statuses.pending, color: '#ff9800' },
            { name: 'Rejected', value: statuses.rejected, color: '#f44336' },
        ];
    }, [applications]);

    const scoreData = useMemo(() => {
        const ranges = { '300-499': 0, '500-599': 0, '600-699': 0, '700-799': 0, '800-900': 0 };
        applications.forEach(app => {
            if (app.creditScore) {
                if (app.creditScore < 500) ranges['300-499']++;
                else if (app.creditScore < 600) ranges['500-599']++;
                else if (app.creditScore < 700) ranges['600-699']++;
                else if (app.creditScore < 800) ranges['700-799']++;
                else ranges['800-900']++;
            }
        });
        return Object.entries(ranges).map(([name, value]) => ({ name, count: value }));
    }, [applications]);

    return (
        <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
            <Paper sx={{ p: 2, height: '400px', flex: '1 1 400px', minWidth: '400px' }}>
                <Typography variant="h6">Application Status Breakdown</Typography>
                <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                        <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} label>
                            {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </Paper>
            <Paper sx={{ p: 2, height: '400px', flex: '1 1 500px', minWidth: '500px' }}>
                <Typography variant="h6">Credit Score Distribution</Typography>
                <ResponsiveContainer width="100%" height="90%">
                    <BarChart data={scoreData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#8884d8" name="No. of Applications" />
                    </BarChart>
                </ResponsiveContainer>
            </Paper>
        </Box>
    );
};
// #endregion

const ReportsPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle URL parameter for tab navigation
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabNumber = parseInt(tabParam, 10);
      if (!isNaN(tabNumber) && tabNumber >= 0 && tabNumber <= 10) {
        setTabIndex(tabNumber);
      }
    }
  }, [searchParams]);


  return (
    <Box sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        {t('reports.title')}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns}>

        {tabIndex === 0 && <ApplicationReports />}
        {tabIndex === 1 && <SystemReports />}
        {tabIndex === 2 && <Visualizations />}
        {tabIndex === 3 && <PortfolioHealth />}
        {tabIndex === 4 && <TransactionReports />}
        {tabIndex === 5 && <InactiveAccountAlerts />}
        {tabIndex === 6 && <AccountLinkingComponent />}
        {tabIndex === 7 && <LoanHistory />}
        {tabIndex === 8 && (
          <Box>
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              Debug: RegulatoryReports tab is active (index {tabIndex})
            </Typography>
            <RegulatoryReports />
          </Box>
        )}
        {tabIndex === 9 && <KYCReports />}
        {tabIndex === 10 && <AdvancedRiskDashboard />}
      </LocalizationProvider>
    </Box>
  );
};

export default ReportsPage; 